<template>
  <div class="ibb-sheet">
    <div class="ibb-sheet__topbar">
      <button class="ibb-sheet__back" @click="goBack" :title="$t('buttons.close') || 'Cerrar'">
        ←
      </button>
      <span class="ibb-sheet__filename">{{ filename }}</span>
      <span class="ibb-sheet__status" :class="statusClass">
        <span class="ibb-sheet__status-dot" v-if="statusIconKind"></span>
        {{ statusText }}
      </span>
      <div class="ibb-sheet__export-wrap" v-click-outside="closeExportMenu">
        <button
          class="ibb-sheet__history"
          @click="toggleExportMenu"
          :title="$t('files.exportMenu') || 'Exportar e imprimir'"
        >
          <span class="ibb-sheet__history-icon">⬇</span>
          <span class="ibb-sheet__history-text">{{ $t('files.exportMenu') || 'Exportar' }}</span>
        </button>
        <ul v-if="showExportMenu" class="ibb-sheet__export-menu">
          <li @click="exportOriginal">{{ $t('files.downloadOriginal') || 'Descargar original' }}</li>
          <li @click="printTxt">{{ $t('files.print') || 'Imprimir' }}</li>
        </ul>
      </div>
      <button
        v-if="!isReadOnly"
        class="ibb-sheet__history"
        @click="showHistory = true"
        :title="$t('files.versionHistory') || 'Historial de versiones'"
      >
        <span class="ibb-sheet__history-icon">⟲</span>
        <span class="ibb-sheet__history-text">{{ $t('files.versionHistory') || 'Historial' }}</span>
      </button>
      <button
        v-if="!isReadOnly"
        class="ibb-sheet__save"
        :disabled="saving || !dirty"
        @click="save"
      >
        {{ $t('buttons.save') || 'Guardar' }}
      </button>
    </div>

    <div ref="mount" class="ibb-sheet__mount ibb-sheet__mount--text">
      <div id="editor" class="ibb-editor__ace"></div>
    </div>

    <div v-if="loaded" class="ibb-sheet__statusbar">
      <span class="ibb-sheet__stat">
        <strong>{{ stats.lines }}</strong>
        {{ $t('files.linesCount') || 'líneas' }}
      </span>
      <span class="ibb-sheet__stat-sep">•</span>
      <span class="ibb-sheet__stat">
        <strong>{{ stats.words }}</strong>
        {{ $t('files.wordsCount') || 'palabras' }}
      </span>
      <span class="ibb-sheet__stat-sep">•</span>
      <span class="ibb-sheet__stat">
        <strong>{{ stats.chars }}</strong>
        {{ $t('files.charsCount') || 'caracteres' }}
      </span>
      <span class="ibb-sheet__stat-sep">•</span>
      <span class="ibb-sheet__stat" v-if="aceMode">
        {{ $t('files.syntax') || 'Sintaxis' }}: <strong>{{ aceMode }}</strong>
      </span>
    </div>

    <div v-if="!loaded" class="ibb-sheet__loading">
      <div class="ibb-sheet__loading-box">
        <h2>{{ $t('files.loading') || 'Cargando…' }}</h2>
        <p v-if="loadPhase">{{ loadPhase }}</p>
      </div>
    </div>

    <version-history
      v-if="showHistory"
      :node-handle="req && req.handle"
      :parent-handle="req && req.parentHandle"
      :filename="filename"
      :ibb-instance="ibbInstanceForFile"
      @close="showHistory = false"
      @restored="onVersionRestored"
    />

    <div v-if="showUnsavedModal" class="ibb-sheet-modal" @click.self="cancelUnsavedModal">
      <div class="ibb-sheet-modal__card">
        <div class="ibb-sheet-modal__header">
          <h2>{{ $t('prompts.unsavedChangesTitle') || 'Cambios sin guardar' }}</h2>
        </div>
        <div class="ibb-sheet-modal__body">
          {{ $t('prompts.unsavedChanges') || 'Tienes cambios sin guardar. ¿Qué quieres hacer?' }}
        </div>
        <div class="ibb-sheet-modal__footer">
          <button class="ibb-sheet-modal__btn" @click="cancelUnsavedModal">
            {{ $t('buttons.cancel') || 'Cancelar' }}
          </button>
          <button class="ibb-sheet-modal__btn ibb-sheet-modal__btn--save" @click="saveAndLeave">
            {{ $t('prompts.saveAndLeave') || 'Guardar y salir' }}
          </button>
          <button class="ibb-sheet-modal__btn ibb-sheet-modal__btn--discard" @click="discardAndLeave">
            {{ $t('prompts.discardAndLeave') || 'Salir sin guardar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*
 * Editor de texto plano (v2 — 2026-04-17).
 *
 * Reutiliza el shell de SheetEditor/DocsEditor: overlay fullscreen, topbar
 * con back / filename / status / Exportar / Historial / Guardar, modal de
 * cambios sin guardar, barra inferior de estadísticas.
 *
 * Motor: ACE (ya estaba en el stack para este editor). Detecta el modo de
 * sintaxis por la extensión (js, html, css, md, json, py, sh, yaml…).
 *
 * Persistencia:
 *   - `api.putId(handle, text, ibb, onNewHandle)` hace upload + delete con
 *     callback entre las dos operaciones → sincronizamos store/URL antes de
 *     que el delete dispare fetchData.
 *   - `editorSaving` en el store bloquea watchers de $route y reload en las
 *     vistas (Cloud.vue, MyUnits.vue, etc.), evitando desmontajes.
 *   - Autosave 15s tras última edición.
 *   - Historial de versiones compartido con Sheets/Docs vía utils/versionHistory.
 */

import { mapState } from 'vuex';
import { files as api } from '@/api';
import { ROUTES } from '@/router';
import { saveVersionSnapshot } from '@/utils/versionHistory';
import VersionHistory from '@/components/prompts/VersionHistoryXlsx.vue';
import ace from 'ace-builds/src-min-noconflict/ace.js';
import modelist from 'ace-builds/src-min-noconflict/ext-modelist.js';
import 'ace-builds/webpack-resolver';

const AUTOSAVE_MS = 15000;
const VERSION_SNAPSHOT_MS = 120000;

export default {
  name: 'editor',
  components: { VersionHistory },
  directives: {
    'click-outside': {
      bind (el, binding) {
        el.__clickOutsideHandler__ = (evt) => {
          if (!(el === evt.target || el.contains(evt.target))) {
            binding.value(evt);
          }
        };
        document.addEventListener('mousedown', el.__clickOutsideHandler__);
      },
      unbind (el) {
        document.removeEventListener('mousedown', el.__clickOutsideHandler__);
        delete el.__clickOutsideHandler__;
      }
    }
  },
  data () {
    return {
      loaded: false,
      saving: false,
      dirty: false,
      isReadOnly: false,
      loadPhase: '',
      aceEditor: null,
      aceMode: '',
      autosaveTimer: null,
      statsTimer: null,
      lastSavedAt: null,
      lastVersionAt: null,
      showHistory: false,
      showExportMenu: false,
      showUnsavedModal: false,
      stats: { lines: 0, words: 0, chars: 0 },
      // Estado interno para ignoraciones iniciales (la primera carga provoca
      // un evento "change" que NO es mutación del usuario).
      ignoreChange: true,
    };
  },
  computed: {
    ...mapState(['req', 'permissions', 'permissionsSharedDrive']),
    filename () { return (this.req && this.req.name) || ''; },
    ibbInstanceForFile () {
      return this.$route.name === ROUTES.DRIVE
        ? (typeof ibbLink !== 'undefined' ? ibbLink : null)
        : (typeof ibb !== 'undefined' ? ibb : null);
    },
    statusText () {
      if (this.saving) return this.$t('files.saving') || 'Guardando…';
      if (this.dirty) return this.$t('files.unsaved') || 'Cambios sin guardar';
      if (this.lastSavedAt) {
        const hhmm = this.lastSavedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
        return (this.$t('files.savedAt') || 'Guardado a las {time}').replace('{time}', hhmm);
      }
      if (this.loaded) return this.$t('files.saved') || 'Guardado';
      return '';
    },
    statusClass () {
      if (this.saving) return 'ibb-sheet__status--saving';
      if (this.dirty) return 'ibb-sheet__status--dirty';
      if (this.loaded) return 'ibb-sheet__status--ok';
      return '';
    },
    statusIconKind () {
      return this.saving || this.dirty || this.loaded;
    },
  },
  created () {
    window.addEventListener('keydown', this.keyEvent);
    window.addEventListener('beforeunload', this.beforeUnload);
  },
  async mounted () {
    this.isReadOnly = (this.req && this.req.type === 'textImmutable') ||
      (this.permissions === 1 || this.permissionsSharedDrive === 1);
    try {
      this.loadPhase = 'Cargando editor…';
      await this.$nextTick();

      const mode = modelist.getModeForPath(this.filename || '').mode;
      this.aceMode = (mode || 'ace/mode/text').replace('ace/mode/', '');

      // Si `req.content` ya viene resuelto (fetchAction pudo decodificarlo
      // desde la caché local del SDK), lo usamos. Si no (archivo grande,
      // recién creado vacío, recién subido sin caché), lo descargamos.
      let initialContent = (this.req && typeof this.req.content === 'string') ? this.req.content : null;
      if (initialContent === null) {
        this.loadPhase = 'Descargando archivo…';
        try {
          const ibbInstance = this.ibbInstanceForFile;
          const buf = await api.getFileBytes(this.req.handle, ibbInstance);
          const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
          try {
            initialContent = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
          } catch (_) {
            initialContent = '';
          }
        } catch (e) {
          initialContent = '';
          // eslint-disable-next-line no-console
          console.warn('[Editor] no se pudo cargar el archivo, abriendo vacío:', e && e.message);
        }
      }

      this.aceEditor = ace.edit('editor', {
        maxLines: Infinity,
        minLines: 20,
        value: initialContent,
        showPrintMargin: false,
        readOnly: this.isReadOnly,
        theme: 'ace/theme/chrome',
        mode,
        fontSize: '14px',
        fontFamily: 'Menlo, Monaco, \'Courier New\', monospace',
      });

      this.aceEditor.on('change', this.onAceChange);
      this.aceEditor.session.on('changeMode', () => {
        const m = this.aceEditor.session.getMode().$id || '';
        this.aceMode = m.replace('ace/mode/', '');
      });

      this.refreshStats();

      // Tras unos ms dejamos de ignorar los "change" (que dispara la primera carga).
      setTimeout(() => { this.ignoreChange = false; }, 200);

      this.loaded = true;
    } catch (e) {
      this.$showError(e);
      // eslint-disable-next-line no-console
      console.error('[Editor] fallo al inicializar:', e);
    }
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.keyEvent);
    window.removeEventListener('beforeunload', this.beforeUnload);
    clearTimeout(this.autosaveTimer);
    clearTimeout(this.statsTimer);
    if (this.aceEditor) {
      try { this.aceEditor.destroy(); } catch (_) { /* noop */ }
      this.aceEditor = null;
    }
  },
  methods: {
    beforeUnload (e) {
      if (this.dirty) { e.preventDefault(); e.returnValue = ''; return ''; }
    },
    keyEvent (event) {
      if (!event.ctrlKey && !event.metaKey) return;
      if (String.fromCharCode(event.which).toLowerCase() !== 's') return;
      event.preventDefault();
      if (!this.isReadOnly) this.save();
    },
    goBack () {
      if (this.dirty) { this.showUnsavedModal = true; return; }
      this.doLeave();
    },
    doLeave () {
      this.showUnsavedModal = false;
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ path: '/cloud' });
    },
    cancelUnsavedModal () { this.showUnsavedModal = false; },
    async saveAndLeave () {
      this.showUnsavedModal = false;
      try { await this.save(); } catch (_) { /* save reporta error */ }
      if (!this.dirty) this.doLeave();
    },
    discardAndLeave () {
      this.dirty = false;
      this.doLeave();
    },

    onAceChange () {
      this.scheduleStatsRefresh();
      if (this.ignoreChange || this.isReadOnly) return;
      this.markDirty();
    },
    markDirty () {
      this.dirty = true;
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = setTimeout(() => {
        if (this.dirty && !this.saving) this.save();
      }, AUTOSAVE_MS);
    },

    scheduleStatsRefresh () {
      clearTimeout(this.statsTimer);
      this.statsTimer = setTimeout(() => this.refreshStats(), 150);
    },
    refreshStats () {
      if (!this.aceEditor) return;
      const text = this.aceEditor.getValue() || '';
      const lines = text.length ? text.split(/\r\n|\r|\n/).length : 0;
      const words = text.split(/\s+/).filter(Boolean).length;
      const chars = text.length;
      this.stats = { lines, words, chars };
    },

    async save () {
      if (this.isReadOnly || this.saving) return;
      this.saving = true;
      try { this.$store.commit('setEditorSaving', true); } catch (_) { /* noop */ }
      try {
        const text = this.aceEditor ? this.aceEditor.getValue() : '';
        const ibbInstance = this.ibbInstanceForFile;

        const self = this;
        const onNewHandle = (newHandle) => {
          if (!newHandle || !self.req) return;
          try {
            self.$store.commit('updateRequest', { ...self.req, handle: newHandle, content: text });
            if (self.$route && self.$route.hash !== '#' + newHandle) {
              self.$router.replace({ path: self.$route.path, hash: '#' + newHandle });
            }
          } catch (_) { /* noop */ }
        };

        await api.putId(this.req.handle, text, ibbInstance, onNewHandle);

        this.dirty = false;
        this.lastSavedAt = new Date();

        // Historial de versiones estilo 365: snapshot cada >2 min.
        const now = Date.now();
        if (!this.lastVersionAt || (now - this.lastVersionAt.getTime()) > VERSION_SNAPSHOT_MS) {
          try {
            const bytes = new TextEncoder().encode(text);
            await saveVersionSnapshot({
              ibb: ibbInstance,
              parentHandle: this.req && this.req.parentHandle,
              originalName: this.req && this.req.name,
              bytes,
            });
            this.lastVersionAt = new Date();
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[Editor] snapshot versión falló:', err && err.message);
          }
        }
      } catch (e) {
        this.$showError(e);
        // eslint-disable-next-line no-console
        console.error('[Editor] error al guardar:', e);
      } finally {
        this.saving = false;
        setTimeout(() => {
          try { this.$store.commit('setEditorSaving', false); } catch (_) { /* noop */ }
        }, 50);
      }
    },

    async onVersionRestored (bytes) {
      // Los bytes son la versión archivada (utf-8). La cargamos en el editor
      // y disparamos un save — así generamos nuevo handle y queda reflejado.
      this.showHistory = false;
      try {
        const text = new TextDecoder('utf-8').decode(bytes);
        if (this.aceEditor) {
          this.ignoreChange = true;
          this.aceEditor.setValue(text, -1);
          this.dirty = true;
          setTimeout(() => { this.ignoreChange = false; }, 200);
        }
        this.refreshStats();
        await this.save();
      } catch (e) {
        this.$showError(e);
      }
    },

    // ---------- Exportar / Imprimir ----------
    toggleExportMenu () { this.showExportMenu = !this.showExportMenu; },
    closeExportMenu () { this.showExportMenu = false; },
    triggerDownload (bytes, mime, filename) {
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    },
    exportOriginal () {
      this.showExportMenu = false;
      try {
        const text = this.aceEditor ? this.aceEditor.getValue() : '';
        this.triggerDownload(
          new TextEncoder().encode(text),
          'text/plain;charset=utf-8',
          this.filename || 'archivo.txt'
        );
      } catch (e) { this.$showError(e); }
    },
    printTxt () {
      this.showExportMenu = false;
      document.body.classList.add('ibb-sheet-printing');
      setTimeout(() => {
        try { window.print(); } finally {
          setTimeout(() => document.body.classList.remove('ibb-sheet-printing'), 500);
        }
      }, 100);
    },
  },
};
</script>

<style>
/* Estilos compartidos con SheetEditor / DocsEditor. */
@import "../../styles/ibb-editor.css";

.ibb-sheet__mount--text {
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

/* ACE edit wrapper. Queremos que ocupe todo el mount y el cursor quede bien. */
.ibb-editor__ace {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  font-family: Menlo, Monaco, 'Courier New', monospace;
}

/* La versión anterior del editor depositaba ACE directamente sobre un <form>
 * con id="editor". Mantenemos `#editor` como hijo del mount (selector que ACE
 * consume) y aseguramos que ocupa toda la altura disponible. */
#editor {
  width: 100%;
  height: 100%;
  min-height: 400px;
}
</style>
